// =================================================================
// easybooking agent & feature components (Phase 2)
// Chat agent, Voice agent, Booking assistant, Admin voice agent,
// Mail hub, Analytics, Payments.
// =================================================================
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { PaymentMethodsCard, TransferModal, TransactionsCard } from "./PaymentMethods";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Mic, MicOff, PlayCircle, StopCircle, Loader2, Terminal,
  Mail, Plus, Trash2, Paperclip, Inbox as InboxIcon, Printer,
  CreditCard, Download, FileText, TrendingUp, X, CheckCircle2,
  Landmark,
} from "lucide-react";
import { CHANNELS } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =================================================================
   CHAT AGENT (Website inquiries, streaming SSE)
   ================================================================= */
export function ChatAgent({ lang }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hallo! Ich bin der easybooking Chat-Agent. Wie kann ich helfen?" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => "chat-" + Math.random().toString(36).slice(2, 10));
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const r = await fetch(`${API}/agents/chat/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ agent: "chat", session_id: sessionId, message: text, lang }),
      });
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.t) {
              setMessages((m) => {
                const nm = [...m];
                nm[nm.length - 1] = { role: "assistant", text: nm[nm.length - 1].text + j.t };
                return nm;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "[Fehler: " + e.message + "]" }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="chat-agent-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">Chat-Agent (Website)</h2>
        <p className="text-eb-muted text-sm">
          Beantwortet Gast-Anfragen in DE/EN/FR/ES via Claude Sonnet. Shortcode: <code className="font-mono text-amber-400">[easybooking_chat_agent]</code>
        </p>
      </div>
      <div className="card !p-0 h-[60vh] flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-eb-primary text-[#0B0F17] font-medium"
                    : "eb-surface border border-white/5"
                }`}
                data-testid={`chat-msg-${i}`}
              >
                {m.text || <span className="text-eb-muted">…</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-white/5 p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Nachricht an den Chat-Agenten…"
            data-testid="chat-input"
            disabled={streaming}
          />
          <button className="btn btn-primary" onClick={send} disabled={streaming} data-testid="chat-send-button">
            {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}


/* =================================================================
   Shared: microphone recorder hook
   ================================================================= */
function useRecorder(onBlob) {
  const [rec, setRec] = useState(false);
  const chunks = useRef([]);
  const mr = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      mr.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mr.current.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      mr.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        onBlob(blob);
      };
      mr.current.start();
      setRec(true);
    } catch (e) {
      alert("Mikrofon-Zugriff benötigt. " + e.message);
    }
  };
  const stop = () => {
    if (mr.current && mr.current.state !== "inactive") mr.current.stop();
    setRec(false);
  };
  return { rec, start, stop };
}

async function transcribe(blob, lang = "de") {
  const fd = new FormData();
  fd.append("file", blob, "voice.webm");
  fd.append("language", lang);
  const r = await axios.post(`${API}/agents/stt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.text || "";
}
async function speak(text, voice = "nova") {
  const r = await axios.post(`${API}/agents/tts`, { text, voice }, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const audio = new Audio(url);
  await audio.play().catch(() => {});
  return url;
}


/* =================================================================
   VOICE AGENT (Telephony MVP – Browser mic → Whisper → LLM → TTS)
   ================================================================= */
export function VoiceAgent({ lang }) {
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("Bereit. Drücken Sie den roten Knopf und sprechen Sie.");
  const [sessionId] = useState(() => "voice-" + Math.random().toString(36).slice(2, 10));
  const { rec, start, stop } = useRecorder(async (blob) => {
    setBusy(true); setStatus("🎤 Transkription…");
    try {
      const text = await transcribe(blob, lang);
      if (!text) { setStatus("Nichts verstanden."); setBusy(false); return; }
      setTurns((t) => [...t, { role: "user", text }]);
      setStatus("🧠 Claude denkt…");
      const r = await axios.post(`${API}/agents/chat`, {
        agent: "voice", session_id: sessionId, message: text, lang,
      });
      const reply = r.data.reply || "Entschuldigung.";
      setTurns((t) => [...t, { role: "assistant", text: reply }]);
      setStatus("🔊 Antwort wird gesprochen…");
      await speak(reply, "nova");
      setStatus("Bereit.");
    } catch (e) {
      setStatus("Fehler: " + e.message);
    } finally { setBusy(false); }
  });

  return (
    <div className="space-y-4" data-testid="voice-agent-view">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl">Voice-Agent (Telefonie)</h2>
          <p className="text-eb-muted text-sm">Browser-MVP · Whisper + Claude + OpenAI TTS. Für Telekom-SIP siehe Anleitung in <code className="font-mono text-amber-400">sip-telephony/</code>.</p>
        </div>
        <span className="channel-pill" style={{ background: "#10B98122", color: "#10B981", border: "1px solid #10B98144" }}>
          {status}
        </span>
      </div>

      <div className="card min-h-[400px] flex flex-col">
        <div className="flex-1 space-y-3 overflow-y-auto">
          {turns.length === 0 && (
            <div className="text-center text-eb-muted text-sm py-16">
              Noch kein Gespräch. Klicken Sie auf das Mikrofon um zu starten.
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                t.role === "user" ? "bg-eb-primary text-[#0B0F17] font-medium" : "eb-surface border border-white/5"
              }`} data-testid={`voice-turn-${i}`}>
                <div className="overline mb-1">{t.role === "user" ? "ANRUFER" : "AGENT"}</div>
                {t.text}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center items-center py-6">
          <button
            disabled={busy}
            onClick={rec ? stop : start}
            data-testid="voice-record-button"
            className="relative w-20 h-20 rounded-full flex items-center justify-center transition-all"
            style={{
              background: rec ? "#F43F5E" : "#D4AF37",
              boxShadow: rec ? "0 0 0 8px rgba(244,63,94,0.2)" : "0 4px 20px rgba(212,175,55,0.4)",
            }}
          >
            {busy ? <Loader2 size={28} className="animate-spin text-black" /> :
             rec ? <MicOff size={28} className="text-white" /> :
             <Mic size={28} className="text-black" />}
            {rec && <span className="absolute inset-0 rounded-full border-4 border-rose-500/50 animate-ping" />}
          </button>
        </div>
      </div>
    </div>
  );
}


/* =================================================================
   BOOKING ASSISTANT – guides through booking step-by-step
   ================================================================= */
export function BookingAssistant({ lang, properties }) {
  const [sessionId] = useState(() => "book-" + Math.random().toString(36).slice(2, 10));
  const [history, setHistory] = useState([
    { role: "assistant", text: "Hallo! Ich helfe Ihnen Schritt für Schritt bei der Buchung. Für welchen Zeitraum interessieren Sie sich?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (text) => {
    const payload = text || input.trim();
    if (!payload || busy) return;
    setInput(""); setBusy(true);
    setHistory((h) => [...h, { role: "user", text: payload }]);
    try {
      const r = await axios.post(`${API}/agents/chat`, {
        agent: "booking", session_id: sessionId, message: payload, lang,
        context: { properties: properties.map((p) => ({ id: p.easybooking_id, name: p.name, price: p.base_price_eur, sleeps: p.sleeps })) },
      });
      setHistory((h) => [...h, { role: "assistant", text: r.data.reply || "" }]);
    } finally { setBusy(false); }
  };

  const payNow = async (prop) => {
    const origin = window.location.origin;
    const r = await axios.post(`${API}/payments/checkout`, {
      property_id: prop.id, nights: 3, origin_url: origin,
      guest_email: "demo@easybooking.at", guest_name: "Demo Gast",
    });
    window.open(r.data.checkout_url, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="booking-assistant-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">Buchungs-Assistent</h2>
        <p className="text-eb-muted text-sm">KI-Assistent führt den Gast Schritt-für-Schritt zur Buchung — in {lang.toUpperCase()}.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        <div className="card !p-0 h-[55vh] flex flex-col">
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {history.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-eb-primary text-[#0B0F17] font-medium" : "eb-surface border border-white/5"
                }`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="text-eb-muted text-xs">…</div>}
          </div>
          <div className="border-t border-white/5 p-3 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Antwort an den Assistenten…" disabled={busy}
              data-testid="booking-input" />
            <button className="btn btn-primary" onClick={() => send()} disabled={busy} data-testid="booking-send-button">
              <Send size={14} />
            </button>
          </div>
        </div>

        <div className="card">
          <div className="overline mb-3">Schnellbuchung (Demo)</div>
          <div className="space-y-2">
            {properties.map((p) => (
              <button key={p.id} onClick={() => payNow(p)}
                data-testid={`booking-quick-pay-${p.easybooking_id}`}
                className="w-full text-left eb-surface border border-white/5 hover:border-eb-primary rounded p-3 transition">
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="text-xs text-eb-muted">3 Nächte · € {(p.base_price_eur * 3 + (p.cleaning_fee_eur || 0)).toFixed(2)}</div>
                <div className="flex items-center gap-1 mt-1.5 text-[11px] text-eb-primary">
                  <CreditCard size={11} /> Jetzt bezahlen (Card · Klarna · PayPal)
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* =================================================================
   ADMIN VOICE AGENT – admin speaks commands, LLM parses, backend executes
   ================================================================= */
export function AdminAgent({ lang }) {
  const [sessionId] = useState(() => "admin-" + Math.random().toString(36).slice(2, 10));
  const [log, setLog] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const { rec, start, stop } = useRecorder(async (blob) => {
    setBusy(true);
    try {
      const text = await transcribe(blob, lang);
      if (text) await submit(text);
    } finally { setBusy(false); }
  });

  const submit = async (text) => {
    const t = text || input.trim();
    if (!t) return;
    setInput("");
    setLog((l) => [...l, { role: "admin", text: t }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/agents/chat`, {
        agent: "admin", session_id: sessionId, message: t, lang,
      });
      setLog((l) => [...l, { role: "agent", text: r.data.reply, actions: r.data.actions }]);
      // execute each action
      const resList = [];
      for (const a of r.data.actions || []) {
        const er = await axios.post(`${API}/agents/admin/execute`, a);
        resList.push({ action: a, result: er.data });
      }
      setResults((p) => [...resList, ...p].slice(0, 20));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-agent-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">Admin-Agent (Sprachsteuerung)</h2>
        <p className="text-eb-muted text-sm">Sprich oder tippe einen Befehl – Claude parst, Backend führt aus.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        <div className="card !p-0 h-[55vh] flex flex-col">
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {log.length === 0 && (
              <div className="text-eb-muted text-sm">
                Beispiele:<br />
                „Synchronisiere alle Kanäle und zeige die KPIs"<br />
                „Setze den Preis für Chalet Alpenrose vom 1. März bis 10. März auf 580 Euro"<br />
                „Liste alle offenen Anfragen"
              </div>
            )}
            {log.map((e, i) => (
              <div key={i}>
                <div className="overline mb-1">{e.role === "admin" ? "ADMIN" : "AGENT"}</div>
                <div className={`rounded px-3 py-2 text-sm whitespace-pre-wrap font-mono ${
                  e.role === "admin" ? "bg-eb-primary/20 text-eb-primary" : "eb-surface border border-white/5"
                }`} data-testid={`admin-log-${i}`}>
                  {e.text}
                </div>
                {e.actions && e.actions.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {e.actions.map((a, j) => (
                      <span key={j} className="channel-pill" style={{ background: "#38BDF822", color: "#38BDF8", border: "1px solid #38BDF844" }}>
                        <Terminal size={10} className="inline mr-1" /> {a.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 p-3 flex gap-2">
            <button
              onClick={rec ? stop : start}
              disabled={busy}
              className="btn !px-3"
              style={{ background: rec ? "#F43F5E" : "transparent", color: rec ? "#fff" : "inherit" }}
              data-testid="admin-mic-button"
            >
              {rec ? <MicOff size={14} /> : <Mic size={14} />}
            </button>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Befehl eingeben oder Mikrofon nutzen…" disabled={busy}
              data-testid="admin-input" />
            <button className="btn btn-primary" onClick={() => submit()} disabled={busy} data-testid="admin-send-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="overline mb-3">Ausgeführte Aktionen</div>
          <div className="space-y-2 max-h-[48vh] overflow-y-auto">
            {results.length === 0 && <div className="text-eb-muted text-sm">—</div>}
            {results.map((r, i) => (
              <div key={i} className="eb-surface border border-white/5 rounded p-2.5" data-testid={`admin-result-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  <span className="font-mono text-xs text-emerald-400">{r.action.name}</span>
                </div>
                <code className="text-[11px] text-eb-muted font-mono break-all">
                  {JSON.stringify(r.result).slice(0, 200)}
                </code>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* =================================================================
   MAIL HUB – unified inbox over Gmail / Outlook / iCloud
   ================================================================= */
export function MailHub() {
  const [accounts, setAccounts] = useState([]);
  const [presets, setPresets] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [selected, setSelected] = useState(null);
  const [body, setBody] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const a = await axios.get(`${API}/mail/accounts`); setAccounts(a.data);
    const p = await axios.get(`${API}/mail/presets`);  setPresets(p.data);
  };
  const loadInbox = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/mail/inbox?limit=15`); setInbox(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openMsg = async (m) => {
    setSelected(m); setBody(null);
    const r = await axios.get(`${API}/mail/message/${m.account_id}/${m.uid}`);
    setBody(r.data);
  };

  return (
    <div className="space-y-4" data-testid="mail-hub-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl">Mail-Hub</h2>
          <p className="text-eb-muted text-sm">Alle Postfächer in einer Oberfläche · Gmail · Outlook · iCloud · eigene Server</p>
        </div>
        <div className="flex gap-2">
          <button className="btn" onClick={loadInbox} data-testid="mail-refresh-button">
            <InboxIcon size={14} /> {loading ? "…" : "Posteingang aktualisieren"}
          </button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="mail-add-account-button">
            <Plus size={14} /> Konto hinzufügen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_380px_1fr] gap-4">
        {/* Accounts */}
        <div className="card !p-3">
          <div className="overline mb-2 px-1">Konten ({accounts.length})</div>
          {accounts.length === 0 && <div className="text-eb-muted text-xs px-1">Noch kein Konto. Füge Gmail/Outlook/iCloud hinzu.</div>}
          {accounts.map((a) => (
            <div key={a.id} className="flex items-center justify-between px-2 py-2 rounded hover:bg-white/5" data-testid={`mail-account-${a.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{a.label}</div>
                <div className="text-[11px] text-eb-muted truncate">{a.email_addr}</div>
              </div>
              <button onClick={async () => { await axios.delete(`${API}/mail/accounts/${a.id}`); load(); }}
                className="text-eb-muted hover:text-rose-400">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        {/* Inbox list */}
        <div className="card !p-0 overflow-hidden">
          <div className="overline px-3 py-2 border-b border-white/5">Unified Inbox</div>
          <div className="max-h-[55vh] overflow-y-auto">
            {inbox.length === 0 && <div className="text-eb-muted text-xs p-4">Noch keine Nachrichten geladen.</div>}
            {inbox.map((m, i) => (
              <button key={`${m.account_id}-${m.uid ?? i}`} onClick={() => openMsg(m)}
                data-testid={`mail-row-${i}`}
                className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/5 transition ${selected?.uid === m.uid ? "bg-white/[0.04]" : ""}`}>
                {m.error ? (
                  <div className="text-rose-400 text-xs">{m.account_label}: {m.error}</div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium truncate">{m.from}</span>
                      <span className="channel-pill" style={{ background: "#374151", color: "#D1D5DB", fontSize: 9 }}>
                        {m.account_label}
                      </span>
                    </div>
                    <div className="text-sm truncate mt-0.5">{m.subject || "(ohne Betreff)"}</div>
                    <div className="text-[10px] text-eb-muted mt-0.5">{(m.date || "").slice(0, 25)}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Message body */}
        <div className="card">
          {!selected ? (
            <div className="text-eb-muted text-sm text-center py-10">Wähle eine Nachricht aus</div>
          ) : !body ? (
            <div className="text-eb-muted text-sm">Lade…</div>
          ) : (
            <div className="space-y-3">
              <div className="font-heading font-bold text-base">{body.subject}</div>
              <div className="text-xs text-eb-muted">
                <strong>Von:</strong> {body.from} · <strong>An:</strong> {body.to}<br />
                <strong>Datum:</strong> {body.date}
              </div>
              <div className="eb-surface rounded p-3 text-sm max-h-[40vh] overflow-y-auto whitespace-pre-wrap" data-testid="mail-body">
                {body.body?.includes("<") && body.body?.includes(">") ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body.body) }} />
                ) : (
                  body.body
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showAdd && (
          <AddAccountModal presets={presets} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function AddAccountModal({ presets, onClose, onCreated }) {
  const [provider, setProvider] = useState("gmail");
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const preset = presets[provider] || {};
  const save = async () => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/mail/accounts`, {
        provider, label: label || preset.label, email_addr: email, password,
      });
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="card max-w-md w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold">Mail-Konto hinzufügen</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="overline mb-1">Anbieter</div>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="mail-add-provider">
              {Object.entries(presets).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div className="overline mb-1">Bezeichnung</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={preset.label} />
          </div>
          <div>
            <div className="overline mb-1">E-Mail</div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="mail-add-email" />
          </div>
          <div>
            <div className="overline mb-1">App-Passwort</div>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mail-add-password" />
            {preset.help && <div className="text-[11px] text-eb-muted mt-1">{preset.help}</div>}
          </div>
          {err && <div className="text-rose-400 text-xs">{err}</div>}
          <button className="btn btn-primary w-full justify-center" onClick={save} disabled={busy || !email || (!password && provider !== "brevo")} data-testid="mail-add-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Speichern"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


/* =================================================================
   ANALYTICS
   ================================================================= */
export function Analytics() {
  const [channels, setChannels] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    axios.get(`${API}/analytics/revenue-by-channel`).then((r) => setChannels(r.data));
    axios.get(`${API}/analytics/funnel`).then((r) => setFunnel(r.data));
    axios.get(`${API}/analytics/revenue-trend?days=30`).then((r) => setTrend(r.data));
  }, []);
  const maxRev = Math.max(1, ...channels.map((c) => c.revenue_eur));
  const maxFun = Math.max(1, ...funnel.map((f) => f.count));
  const maxTr  = Math.max(1, ...trend.map((t) => t.revenue_eur));

  return (
    <div className="space-y-5" data-testid="analytics-view">
      <div>
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><TrendingUp size={22} /> Analytics</h2>
        <p className="text-eb-muted text-sm">Umsatz pro Kanal · Konversions-Funnel · Umsatz-Trend 30 Tage</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="overline mb-3">Umsatz pro Kanal</div>
          <div className="space-y-2.5">
            {channels.map((c) => {
              const col = CHANNELS[c.channel] || { bg: "#374151", label: c.channel };
              return (
                <div key={c.channel} data-testid={`analytics-ch-${c.channel}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="channel-pill" style={{ background: col.bg, color: col.fg }}>{col.label}</span>
                    <span className="font-mono">€ {c.revenue_eur.toLocaleString("de-DE")} · {c.bookings} Buchungen</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded">
                    <div className="h-full rounded" style={{ width: `${(c.revenue_eur / maxRev) * 100}%`, background: col.bg }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="overline mb-3">Konversions-Funnel</div>
          <div className="space-y-2">
            {funnel.map((f, i) => (
              <div key={f.stage} data-testid={`analytics-funnel-${f.stage}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span>{f.stage}</span>
                  <span className="font-mono">{f.count}</span>
                </div>
                <div className="h-2 bg-white/5 rounded">
                  <div className="h-full rounded" style={{ width: `${(f.count / maxFun) * 100}%`, background: "linear-gradient(90deg, #D4AF37, #F59E0B)" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="overline mb-3">Umsatz-Trend (30 Tage)</div>
        <svg viewBox="0 0 600 160" className="w-full h-40" preserveAspectRatio="none" data-testid="analytics-trend-chart">
          <defs>
            <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
            </linearGradient>
          </defs>
          {trend.length > 1 && (() => {
            const pts = trend.map((t, i) => {
              const x = (i / (trend.length - 1)) * 600;
              const y = 150 - (t.revenue_eur / maxTr) * 130 - 5;
              return [x, y];
            });
            const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
            const area = line + ` L 600,160 L 0,160 Z`;
            return (<>
              <path d={area} fill="url(#g)" />
              <path d={line} stroke="#D4AF37" strokeWidth="2" fill="none" />
            </>);
          })()}
        </svg>
      </div>
    </div>
  );
}


/* =================================================================
   PAYMENTS – list transactions, pay from booking rows (card/klarna/paypal)
   ================================================================= */
export function Payments({ properties }) {
  const [bookings, setBookings] = useState([]);
  const [transfer, setTransfer] = useState(null);
  const [payMsg, setPayMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/bookings`).then((r) => setBookings(r.data));
  }, []);

  const pay = async (b) => {
    const r = await axios.post(`${API}/payments/checkout`, {
      booking_id: b.id, origin_url: window.location.origin,
    });
    if (r.data.unavailable?.length) { setPayMsg(`Hinweis: ${r.data.unavailable.join(", ")} im Stripe-Dashboard noch nicht aktiviert – Checkout ohne diese Zahlart geöffnet`); setTimeout(() => setPayMsg(""), 6000); }
    window.open(r.data.checkout_url, "_blank");
  };
  const pdf = (bid, type) => window.open(`${API}/pdf/booking/${bid}/${type}`, "_blank");

  return (
    <div className="space-y-5" data-testid="payments-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">Zahlungen & Dokumente</h2>
        <p className="text-eb-muted text-sm">Stripe-Checkout · Kreditkarte, Klarna, PayPal, SEPA, eps · Überweisung mit GiroCode · PDF-Export</p>
        {payMsg && <div className="text-amber-400 text-xs mt-1" data-testid="pay-msg">{payMsg}</div>}
      </div>
      <PaymentMethodsCard />
      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="eb-surface">
              <th className="text-left px-4 py-3 overline">Gast</th>
              <th className="text-left px-4 py-3 overline">Objekt</th>
              <th className="text-left px-4 py-3 overline">Zeitraum</th>
              <th className="text-right px-4 py-3 overline">Betrag</th>
              <th className="text-right px-4 py-3 overline">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {bookings.slice(0, 10).map((b) => {
              const prop = properties.find((p) => p.id === b.property_id);
              return (
                <tr key={b.id} className="border-t border-white/5" data-testid={`payment-row-${b.id}`}>
                  <td className="px-4 py-2.5">{b.guest_name}</td>
                  <td className="px-4 py-2.5 text-eb-muted">{prop?.name || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{b.check_in} → {b.check_out}</td>
                  <td className="px-4 py-2.5 text-right font-mono">€ {b.total_eur.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1.5">
                      <button className="btn !py-1 !px-2" onClick={() => pdf(b.id, "confirmation")} data-testid={`pdf-confirm-${b.id}`}>
                        <FileText size={11} /> Bestätigung
                      </button>
                      <button className="btn !py-1 !px-2" onClick={() => pdf(b.id, "invoice")} data-testid={`pdf-invoice-${b.id}`}>
                        <Printer size={11} /> Rechnung
                      </button>
                      <button className="btn !py-1 !px-2" onClick={() => pdf(b.id, "checkin")} data-testid={`pdf-checkin-${b.id}`}>
                        <Download size={11} /> Check-in
                      </button>
                      <button className="btn btn-primary !py-1 !px-2" onClick={() => pay(b)} data-testid={`stripe-pay-${b.id}`}>
                        <CreditCard size={11} /> Jetzt bezahlen
                      </button>
                      <button className="btn !py-1 !px-2" onClick={() => setTransfer(b)} data-testid={`transfer-pay-${b.id}`}>
                        <Landmark size={11} /> Überweisung
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TransactionsCard />
      {transfer && <TransferModal booking={transfer} onClose={() => setTransfer(null)} />}
      <div className="text-xs text-eb-muted">
        Scanner-Hinweis: Für Scan-Funktionen aus WordPress werden <strong>Canon</strong>, <strong>HP</strong>, <strong>Brother</strong>, <strong>Epson</strong>, <strong>Kyocera</strong> und <strong>Xerox</strong> unterstützt (SANE/TWAIN-Treiber auf dem Server, Scan-Befehl via easybooking → Druck/Scan-Modul).
      </div>
    </div>
  );
}
